import { useState, type FormEvent } from "react";

type SubmitStatus = "idle" | "submitting" | "success" | "error";

interface FormResponse {
  code?: string;
  message?: string;
  meta?: {
    message?: string;
    detail?: string;
  };
}

const SUBMIT_URL = "https://readdy.ai/api/form/daiio83oh653ivfvoe0g";

export default function ContactForm() {
  const [status, setStatus] = useState<SubmitStatus>("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);

    const honeypot = String(data.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setFormError("");
      form.reset();
      return;
    }
    data.delete("website_alt");

    const params = new URLSearchParams();
    data.forEach((value, key) => {
      if (typeof value === "string") params.append(key, value);
    });

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(SUBMIT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed: FormResponse | null = null;
      try {
        parsed = JSON.parse(responseText) as FormResponse;
      } catch {
        parsed = null;
      }

      const code = parsed?.code;
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (response.ok && code === "OK" && !isSpam) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(
          typeof serverMsg === "string" && serverMsg.trim()
            ? serverMsg
            : "提交失败，请检查填写内容后再试一次。",
        );
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交失败，请稍后再试。");
    }
  };

  return (
    <div className="rounded-lg border border-background-300/60 bg-background-50 p-6 md:p-8">
      <h3 className="font-heading text-lg font-bold text-foreground-950">线索投稿与联系</h3>
      <p className="mt-2 text-sm leading-7 text-foreground-600">
        有更完整的时间线、现场信息，或发现内容需要更正？填写下面的表单告诉我们，我们会逐条核对。
      </p>

      <form id="contact-form" data-readdy-form className="mt-6 space-y-4" onSubmit={handleSubmit} noValidate={false}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-800">
              称呼 <span className="text-primary-500">*</span>
            </label>
            <input
              id="contact-name"
              name="name"
              type="text"
              required
              maxLength={40}
              placeholder="怎么称呼你"
              className="mt-2 w-full rounded-md border border-background-400/70 bg-background-100 px-3.5 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
            />
          </div>
          <div>
            <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-800">
              邮箱 <span className="text-primary-500">*</span>
            </label>
            <input
              id="contact-email"
              name="email"
              type="email"
              required
              placeholder="you@example.com"
              className="mt-2 w-full rounded-md border border-background-400/70 bg-background-100 px-3.5 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
            />
          </div>
        </div>

        <div>
          <label htmlFor="contact-topic" className="block text-sm font-medium text-foreground-800">
            联系类型
          </label>
          <select
            id="contact-topic"
            name="topic"
            defaultValue="线索补充"
            className="mt-2 w-full rounded-md border border-background-400/70 bg-background-100 px-3.5 py-2.5 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200 cursor-pointer"
          >
            <option value="线索补充">线索补充</option>
            <option value="内容纠错">内容纠错</option>
            <option value="合作咨询">合作咨询</option>
            <option value="其他建议">其他建议</option>
          </select>
        </div>

        <div>
          <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-800">
            具体说明 <span className="text-primary-500">*</span>
          </label>
          <textarea
            id="contact-message"
            name="message"
            required
            maxLength={500}
            rows={5}
            placeholder="请尽量描述清楚相关话题、时间与你的补充信息（最多 500 字）"
            className="mt-2 w-full rounded-md border border-background-400/70 bg-background-100 px-3.5 py-2.5 text-sm leading-6 text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200 resize-none"
          />
          <p className="mt-1.5 text-xs text-foreground-500">最多 500 字，越具体越便于我们核对。</p>
        </div>

        <input
          className="form-honeypot-field"
          type="text"
          name="website_alt"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          readOnly
        />

        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <button
            type="submit"
            disabled={status === "submitting"}
            className="inline-flex items-center justify-center gap-2 rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <span className="w-4 h-4 flex items-center justify-center">
              <i className={status === "submitting" ? "ri-loader-4-line animate-spin" : "ri-send-plane-line"} aria-hidden="true"></i>
            </span>
            {status === "submitting" ? "正在提交..." : "提交信息"}
          </button>

          {status === "success" && (
            <p className="inline-flex items-center gap-2 text-sm text-secondary-700">
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-checkbox-circle-line" aria-hidden="true"></i>
              </span>
              已收到你的信息，我们会尽快核对并回复。
            </p>
          )}
          {status === "error" && (
            <p className="inline-flex items-center gap-2 text-sm text-primary-600">
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-error-warning-line" aria-hidden="true"></i>
              </span>
              {formError}
            </p>
          )}
        </div>
      </form>
    </div>
  );
}